import React from 'react'
import { Box, Flex, Text } from '@chakra-ui/core'
import { Check } from 'lucide-react'

export type SelectMode = 'single' | 'multiple'

export interface ISelectItemProps {
  label: React.ReactNode
  subtitle?: React.ReactNode
  trailingElement?: React.ReactNode
  indicatorPosition?: 'start' | 'end'
  leftElement?: React.ReactNode
  isSelected: boolean
  mode?: SelectMode
  isRtl?: boolean
}

const Indicator: React.FC<{ isSelected: boolean; mode: SelectMode }> = ({ isSelected, mode }) => (
  <Box flexShrink={0} display="flex" alignItems="center">
    {mode === 'multiple' ? (
      <Box
        w="16px"
        h="16px"
        borderRadius="4px"
        borderWidth="1.5px"
        borderStyle="solid"
        borderColor={isSelected ? '#0F172A' : '#CBD5E1'}
        bg={isSelected ? '#0F172A' : 'transparent'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ transition: 'all 0.12s ease-out', flexShrink: 0 }}
      >
        {isSelected && <Check size={10} color="white" strokeWidth={3} />}
      </Box>
    ) : (
      <Box
        w="16px"
        h="16px"
        borderRadius="50%"
        borderWidth="1.5px"
        borderStyle="solid"
        borderColor={isSelected ? '#0F172A' : '#CBD5E1'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ transition: 'all 0.12s ease-out', flexShrink: 0 }}
      >
        {isSelected && <Box w="8px" h="8px" borderRadius="50%" bg="#0F172A" />}
      </Box>
    )}
  </Box>
)

export const SelectItem: React.FC<ISelectItemProps> = ({
  label,
  subtitle,
  trailingElement,
  indicatorPosition = 'end',
  leftElement,
  isSelected,
  mode = 'multiple',
  isRtl = false,
}) => {
  const textContent = (
    <Box flex={1} overflow="hidden" mx="8px">
      <Text
        fontSize="13px"
        fontWeight="400"
        color="#0F172A"
        lineHeight="1.4"
        textAlign={indicatorPosition === 'start' ? 'start' : isRtl ? 'right' : 'left'}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </Text>
      {subtitle && (
        <Text
          fontSize="11px"
          color="#94A3B8"
          isTruncated
          lineHeight="1.3"
          textAlign={indicatorPosition === 'start' ? 'start' : isRtl ? 'right' : 'left'}
        >
          {subtitle}
        </Text>
      )}
    </Box>
  )

  // Legacy end indicators mirror their child order explicitly; leading indicators inherit reading direction.
  return (
    <Flex align="center" justify="space-between" w="100%" dir={indicatorPosition === 'end' ? 'ltr' : undefined}>
      {indicatorPosition === 'start' ? (
        <>
          <Indicator isSelected={isSelected} mode={mode} />
          {textContent}
          {trailingElement && (
            <Box flexShrink={0} fontSize="11px" color="#94A3B8">
              {trailingElement}
            </Box>
          )}
        </>
      ) : isRtl ? (
        <>
          <Indicator isSelected={isSelected} mode={mode} />
          {textContent}
          {leftElement && (
            <Box flexShrink={0} display="flex" alignItems="center">
              {leftElement}
            </Box>
          )}
        </>
      ) : (
        <>
          {leftElement && (
            <Box flexShrink={0} display="flex" alignItems="center">
              {leftElement}
            </Box>
          )}
          {textContent}
          <Indicator isSelected={isSelected} mode={mode} />
        </>
      )}
    </Flex>
  )
}
